import { classNames } from 'primereact/utils';
import React, { useContext, useEffect, useRef } from 'react';
import { LayoutContext } from './context/layoutcontext';
import { MenuContext } from './context/menucontext';
import { useSubmenuOverlayPosition } from './hooks/useSubmenuOverlayPosition';
import { useLocation, NavLink } from 'react-router-dom';
import { SCIcon } from '../../Common/SCIcon';
import { Ripple } from "../../Common/Imports";
import { AppMenuItemProps } from '../../Models/Template';
import { PermissionHelper, usePermissions } from '../../Helpers/Permissions/PermissionContext';

const AppMenuItem = (props: AppMenuItemProps) => {
    const { activeMenu, setActiveMenu } = useContext(MenuContext);
    const { isSlim, isSlimPlus, isHorizontal, isDesktop, setLayoutState, layoutState, layoutConfig } = useContext(LayoutContext);
    const location = useLocation();
    const submenuRef = useRef<HTMLUListElement>(null);
    const menuitemRef = useRef<HTMLLIElement>(null);
    const item = props.item;
    const key = props.parentKey ? props.parentKey + '-' + props.index : String(props.index);
    const isActiveRoute = item!.path && location.pathname === item!.path;
    const active = activeMenu === key || !!(activeMenu && activeMenu.startsWith(key + '-'));
    const { userRole } = usePermissions(); // Call usePermissions here at the top level


    useSubmenuOverlayPosition({
        target: menuitemRef.current,
        overlay: submenuRef.current,
        container: menuitemRef.current && menuitemRef.current.closest('.layout-menu-container'),
        when: props.root && active && (isSlim() || isSlimPlus() || isHorizontal()) && isDesktop()
    });

    useEffect(() => {
        if (layoutState.resetMenu) {
            setActiveMenu('');
            setLayoutState((prevLayoutState) => ({
                ...prevLayoutState,
                resetMenu: false
            }));
        }
    }, [layoutState.resetMenu, setActiveMenu, setLayoutState]);

    useEffect(() => {
        if (!(isSlim() || isSlimPlus() || isHorizontal()) && isActiveRoute) {
            setActiveMenu(key);
        }
        const url = location.pathname + location.search;
        const onRouteChange = () => {
            if (!(isSlim() || isHorizontal()) && item!.path && item!.path === url) {
                setActiveMenu(key);
            }
        };
        onRouteChange();
    }, [location.pathname, location.search, layoutConfig, isSlim, isSlimPlus, isHorizontal, isActiveRoute, setActiveMenu, key, item]);

    const itemClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        //avoid processing disabled items
        if (item!.disabled) {
            event.preventDefault();
            return;
        }

        // navigate with hover
        if (props.root && (isSlim() || isHorizontal() || isSlimPlus())) {
            const isSubmenu = event.currentTarget.closest('.layout-root-menuitem.active-menuitem > ul') !== null;
            if (isSubmenu)
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: true
                }));
            else
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: !prevLayoutState.menuHoverActive
                }));
        }

        // toggle active state
        if (item?.items) {
            setActiveMenu(active ? props.parentKey! : key);

            if (props.root && !active && (isSlim() || isHorizontal() || isSlimPlus())) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    overlaySubmenuActive: true
                }));
            }
        } else {
            if (!isDesktop()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive
                }));
            }

            if (isSlim() || isSlimPlus() || isHorizontal()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    menuHoverActive: false
                }));
            }

            setActiveMenu(key);
        }
    };

    const onMouseEnter = () => {
        // activate item on hover
        if (props.root && (isSlim() || isHorizontal() || isSlimPlus()) && isDesktop()) {
            if (!active && layoutState.menuHoverActive) {
                setActiveMenu(key);
            }
        }
    };

    const subMenu =
        item?.items && item?.visible !== false ? (
            <ul ref={submenuRef} className={classNames({ 'layout-root-submenulist': props.root })}>
                {item?.items.map((child, i) => {
                    return <AppMenuItem item={child} index={i} className={child.badgeClass} parentKey={key} key={child.label} />;
                })}
            </ul>
        ) : null;

    return (
        item &&
        <li
            ref={menuitemRef}
            className={classNames({
                'layout-root-menuitem': props.root,
                'active-menuitem': active
            })}
        >
            {props.root && item?.visible !== false && (
                <PermissionHelper requiredPermission={{ idAspNetRole: userRole, idItem: item.id, idPermission: 2 }}>
                    <div className="layout-menuitem-root-text">
                        <span>{item?.label}</span>
                        <i className="layout-menuitem-root-icon pi pi-fw pi-ellipsis-h"></i>
                    </div>
                </PermissionHelper>

            )}
            {(!item?.path || item?.items) && item?.visible !== false ? (
                <PermissionHelper requiredPermission={{ idAspNetRole: userRole, idItem: item.id, idPermission: 2 }}>
                    <a
                        href={item?.url}
                        onClick={(e) => itemClick(e)}
                        className={classNames(item?.class, 'p-ripple tooltip-target')}
                        target={item?.target}
                        data-pr-tooltip={item?.label}
                        data-pr-disabled={!(isSlim() && props.root && !layoutState.menuHoverActive)}
                        tabIndex={0}
                        onMouseEnter={onMouseEnter}
                    >
                        <SCIcon Classes="me-2" Name={item?.icon ? item.icon : "MenuItemNoIcon"} />
                        <span className="layout-menuitem-text">{item?.label}</span>
                        {
                            item?.items && item?.items?.length > 0
                            &&
                            <SCIcon Name="ChevronDown" Classes=" layout-submenu-toggler" />
                        }
                        <Ripple />
                    </a>
                </PermissionHelper>
            ) : null}

            {item?.path && !item?.items && item?.visible !== false ? (
                <PermissionHelper requiredPermission={{ idAspNetRole: userRole, idItem: item.id, idPermission: 2 }}>
                    <NavLink
                        to={item?.path}
                        replace={item?.replaceUrl}
                        onClick={(e) => itemClick(e)}
                        className={({ isActive }) =>
                            classNames(item?.class, 'p-ripple', {
                                'active-route': isActive
                            })
                        }
                        tabIndex={0}
                        onMouseEnter={onMouseEnter}
                    >
                        <SCIcon Classes="me-2" Name={item.icon ? item.icon : "MenuItemNoIcon"} />
                        <span className="layout-menuitem-text">{item?.label}</span>
                        {item?.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                        <Ripple />
                    </NavLink>
                </PermissionHelper>
            ) : null}
            {subMenu}
        </li>
    );
};

export default AppMenuItem;
